import { Link } from 'react-router-dom';
import { useSlackActivity } from '@/lib/api/connectors';
import { Card } from '@/components/ui/Card';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useWorkspacePath } from '@/lib/workspacePath';
import type { SlackActivityStatus } from '@/lib/api/types';

/** Short human labels for the slack activity statuses (spec 0011, FR-7). */
const STATUS_LABELS: Record<SlackActivityStatus, string> = {
  processing: 'Processing',
  triggered: 'Triggered',
  skipped_bot: 'Skipped: bot message',
  skipped_subtype: 'Skipped: message subtype',
  skipped_disabled: 'Skipped: disabled',
  skipped_channel: 'Skipped: channel',
  skipped_rate_limited: 'Skipped: rate limited',
  failed: 'Failed',
};

interface SlackActivityPanelProps {
  connectorId: string;
  /** The connector's designated responder agent; used to build transcript links. */
  agentId: string;
}

/**
 * Recent Slack events routed at this connector (spec 0011, FR-7). Newest
 * first. A triggered row with a session links to the conversation transcript
 * (the existing chat view, loaded via ?session=); a failed row shows why.
 */
export function SlackActivityPanel({ connectorId, agentId }: SlackActivityPanelProps) {
  const ws = useWorkspacePath();
  const activity = useSlackActivity(connectorId);
  const items = activity.data ?? [];

  return (
    <Card
      title="Recent activity"
      description="Slack events routed at this connector, newest first."
    >
      <QueryState
        isLoading={activity.isLoading}
        error={activity.error}
        isEmpty={items.length === 0}
        emptyMessage="No Slack activity yet."
        loadingLabel="Loading Slack activity"
        skeleton={<SkeletonRows rows={3} />}
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
              <th className="py-2">Channel</th>
              <th className="py-2">Time</th>
              <th className="py-2">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id} data-testid="slack-activity-row">
                <td className="py-2 font-mono text-xs text-gray-700">{item.channel}</td>
                <td className="py-2">{new Date(item.received_at).toLocaleString()}</td>
                <td className="py-2">
                  <span data-testid="slack-activity-status">
                    {STATUS_LABELS[item.status] ?? item.status}
                  </span>
                  {item.status === 'failed' && item.failure_reason && (
                    <p className="mt-0.5 text-xs text-red-700" data-testid="slack-activity-failure">
                      {item.failure_reason}
                    </p>
                  )}
                </td>
                <td className="py-2 text-right">
                  {item.status === 'triggered' && item.session_id && (
                    <Link
                      to={`${ws('agents', agentId, 'chat')}?session=${item.session_id}`}
                      className="text-brand-600 hover:underline"
                      data-testid="slack-view-conversation-link"
                    >
                      View conversation
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </Card>
  );
}
